import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { booleanToTextValue } from "~/app/utils/helpers"
import { RADIO_VALUE_YES } from "../utils/constants"
import {
  CalculationQueryFields,
  CalculationVAT,
  QuoteCycle,
} from "~/shared/components/CostCalculator/types"
import { SalaryCalculation, SelectedSalaryEnumType } from "@oysterhr/schema"

export const useCalculationsParams = () => {
  const [params, setParams] = useSearchParams()

  const addVATParams = useCallback(
    (data: CalculationVAT) => {
      const { serviceWithinCountry, salesWithinCountry, salesPercentage } = data
      // Get the number of calculations by getting country codes
      const calculationsLength = params.getAll("countryCode").length
      const calculationIndex = Number(params.get("activeCalculationIndex")) || 0

      // Get current list of values or create a new one if there is none
      const vatServices =
        params.get("vatServices")?.split(",") ?? new Array(calculationsLength).fill("")
      const vatSales = params.get("vatSales")?.split(",") ?? new Array(calculationsLength).fill("")
      const vatPercentages =
        params.get("vatPercentages")?.split(",") ?? new Array(calculationsLength).fill("")

      // Update values for specific calculation
      vatServices[calculationIndex] = serviceWithinCountry
      vatSales[calculationIndex] = salesWithinCountry
      vatPercentages[calculationIndex] = salesPercentage

      const updatedParams = new URLSearchParams(params.toString())

      updatedParams.set("vatServices", vatServices.join(","))
      updatedParams.set("vatSales", vatSales.join(","))
      updatedParams.set("vatPercentages", vatPercentages.join(","))

      setParams(updatedParams)

      return updatedParams
    },
    [setParams, params],
  )

  const updateURLWithCalculations = useCallback(
    (calculations: SalaryCalculation[], cycle: QuoteCycle, activeCalculationIndex: number) => {
      if (calculations.length > 0) {
        const countries: string[] = []
        const salaries: number[] = []
        const currencies: string[] = []
        const states: string[] = []
        const vatServices: string[] = []
        const vatSales: string[] = []
        const vatPercentages: string[] = []

        calculations.forEach(calculation => {
          countries.push(calculation.country.code)
          salaries.push(calculation.annualGrossSalary)
          currencies.push(calculation.currency.code)
          states.push(calculation.stateOrProvince || "")
          vatServices.push(
            calculation.vatParameters
              ? booleanToTextValue(calculation.vatParameters?.worksInCountryOfResidence)
              : "",
          )
          vatSales.push(
            calculation.vatParameters?.salesToCountry != null
              ? booleanToTextValue(calculation.vatParameters.salesToCountry)
              : "",
          )
          vatPercentages.push(calculation.vatParameters?.salesPercentageToCountry?.toString() || "")
        })

        const updatedParams = new URLSearchParams(params.toString())

        // Set calculations URL and preserve the other params
        updatedParams.set("countryCode", countries.join(","))
        updatedParams.set("annualGrossSalary", salaries.join(","))
        updatedParams.set("currencyCode", currencies.join(","))
        updatedParams.set("stateOrProvince", states.join(","))
        updatedParams.set("activeCalculationIndex", activeCalculationIndex.toString())
        updatedParams.set("cycle", cycle)
        updatedParams.set("vatServices", vatServices.join(","))
        updatedParams.set("vatSales", vatSales.join(","))
        updatedParams.set("vatPercentages", vatPercentages.join(","))

        setParams(updatedParams)
      }
    },
    [params, setParams],
  )

  const resetParams = () => {
    setParams(new URLSearchParams())
  }

  const setParam = (name: string, value: string) => {
    const updatedParams = new URLSearchParams(params.toString())
    updatedParams.set(name, encodeURIComponent(value))
    setParams(updatedParams)
  }

  const setTotalCurrencyParam = (currency: string) => {
    setParam("totalCurrency", currency)
  }

  const getParam = useCallback(
    (name: string) => {
      const param = params.get(name)
      if (param) return decodeURIComponent(param)
    },
    [params],
  )

  const parseUrlToCalculationQueries = useCallback((): CalculationQueryFields[] => {
    const salaries = params.get("annualGrossSalary")?.split(",")
    const countries = params.get("countryCode")?.split(",")
    const currencies = params.get("currencyCode")?.split(",")
    const states = params.get("stateOrProvince")?.split(",")
    const salesPercentage = params.get("vatPercentages")?.split(",")
    const serviceWithinCountry = params.get("vatServices")?.split(",")
    const salesWithinCountry = params.get("vatSales")?.split(",")

    let calculationQueries: CalculationQueryFields[] = []

    if (salaries && countries && currencies) {
      calculationQueries = salaries.map((salary, index) => ({
        annualGrossSalary: parseFloat(salary),
        countryCode: countries[index] || "",
        currencyCode: currencies[index] || "",
        stateOrProvince: states?.[index] !== "" ? states?.[index] : undefined,
        selectedSalary: SelectedSalaryEnumType.Gross,
        vatParameters: serviceWithinCountry?.[index]
          ? {
              salesPercentageToCountry: salesPercentage?.[index]
                ? Number(salesPercentage?.[index])
                : undefined,
              worksInCountryOfResidence: serviceWithinCountry?.[index]
                ? serviceWithinCountry?.[index] === RADIO_VALUE_YES
                : undefined,
              salesToCountry: salesWithinCountry?.[index]
                ? salesWithinCountry[index] === RADIO_VALUE_YES
                : undefined,
            }
          : undefined,
      }))
    }

    return calculationQueries
  }, [params])

  const isPublicRoute = () => {
    return window.location.pathname.includes("/cost-calculator")
  }

  return {
    params,
    addVATParams,
    resetParams,
    updateURLWithCalculations,
    setTotalCurrencyParam,
    getParam,
    parseUrlToCalculationQueries,
    isPublicRoute,
  }
}
